type InputSlot = { inputProps?: Record<string, unknown> } | undefined;

const addTestId = (slot: InputSlot, testId: string) => ({
  ...slot,
  inputProps: { ...slot?.inputProps, 'data-testid': testId },
});

/**
 * Puts a picker's `data-testid` on the `<input>` it renders.
 *
 * MUI X does not forward unknown props to the text field inside its pickers,
 * so a `data-testid` spread onto one never reaches the DOM (final review M4,
 * and again on 2026-10-14 for eleven date/date-time fields). Its one sanctioned
 * channel is `slotProps.textField.inputProps`, merged into — never replacing —
 * whatever slots the caller passed. Shared by every `@mfw/forms` picker so the
 * rule lives in one place.
 *
 * MUI also takes that slot as a function of the field's owner state; it is
 * wrapped, not spread, or everything it returns would be lost.
 */
export const withInputTestId = <TSlotProps extends object | undefined>(
  slotProps: TSlotProps,
  testId?: string,
) => {
  if (!testId) return slotProps;
  const textField = (slotProps as { textField?: unknown } | undefined)
    ?.textField;
  return {
    ...slotProps,
    textField:
      typeof textField === 'function'
        ? (ownerState: unknown) =>
            addTestId(
              (textField as (state: unknown) => InputSlot)(ownerState),
              testId,
            )
        : addTestId(textField as InputSlot, testId),
  } as TSlotProps;
};
